/**
 * MathRenderer.jsx
 * Renders mathematical content using MathJax 3.
 *
 * Renders LaTeX/math for ALL subjects (Physics, Chemistry, Mathematics, etc.)
 * isMathSubject is kept as a named export for Verify.jsx edit-lock logic.
 */
import { useEffect, useRef, useState } from 'react';

// Subject ID for Mathematics — used only for Verify.jsx edit-lock, NOT for rendering gate
const MATH_SUBJECT_ID = 3;

// ─────────────────────────────────────────────────────────────────────────────
// isMathSubject(subjectId)
// Used by Verify.jsx to decide edit-lock behaviour. Not used for rendering.
// ─────────────────────────────────────────────────────────────────────────────
export function isMathSubject(subjectId) {
  if (!subjectId) return false;
  return Number(subjectId) === MATH_SUBJECT_ID;
}

// ─────────────────────────────────────────────────────────────────────────────
// hasMath(text)
// Returns true if text contains LaTeX/math notation.
// Works for ALL subjects — no subject gate.
// ─────────────────────────────────────────────────────────────────────────────
export function hasMath(text, subjectId) {
  if (!text || text.trim().length < 2) return false;

  if (text.includes('$'))       return true;
  if (text.includes('\\('))     return true;
  if (text.includes('\\['))     return true;
  if (text.includes('\\frac'))  return true;
  if (text.includes('\\sqrt'))  return true;
  if (text.includes('\\int'))   return true;
  if (text.includes('\\sum'))   return true;
  if (text.includes('\\lim'))   return true;
  if (text.includes('\\sin'))   return true;
  if (text.includes('\\cos'))   return true;
  if (text.includes('\\tan'))   return true;
  if (text.includes('\\log'))   return true;
  if (text.includes('\\pi'))    return true;
  if (text.includes('\\alpha')) return true;
  if (text.includes('\\beta'))  return true;
  if (text.includes('\\theta')) return true;
  if (text.includes('\\infty')) return true;
  if (text.includes('\\cdot'))  return true;
  if (text.includes('\\times')) return true;

  // Bare caret notation stored in DB: x^2, (1+i)^(2n), a^2 + b^2, etc.
  // Only match when the ^ is between alphanumeric/bracket chars to avoid
  // false positives on English punctuation (e.g. "don't").
  if (/[a-zA-Z0-9)\]]\^[({a-zA-Z0-9-]/.test(text)) return true;

  // Bare fraction-like notation with variables: x/y, (a+b)/(c), Log x/y
  // Requires at least one letter on one side to avoid matching "1/2" in plain text.
  if (/[a-zA-Z]\s*\/\s*[a-zA-Z0-9(\\]|[a-zA-Z0-9)]\s*\/\s*[a-zA-Z(\\]/.test(text)) return true;

  return false;
}

function sanitizeLatex(text) {
  if (!text) return '';
  let t = text;
  t = t.replace(/\\sqrt\s*([a-zA-Z0-9])(?![{(\\])/g, '\\sqrt{$1}');
  t = t.replace(/\bsqrt\(([^)]+)\)/g, '\\sqrt{$1}');
  t = t.replace(/\^([0-9]+)(?!})/g, '^{$1}');
  t = t.replace(/\^([a-zA-Z])(?![{a-zA-Z])/g, '^{$1}');
  t = t.replace(/(\^{[^}]+})\^{([^}]+)}/g, (_, first, second) =>
    `^{${first.slice(2, -1)}^{${second}}}`
  );
  return t;
}

function prepareForMathJax(text) {
  if (!text) return '';
  let t = sanitizeLatex(text);
  if (t.includes('$') || t.includes('\\(') || t.includes('\\[')) return t;
  if (/\\[a-zA-Z]{2,}/.test(t)) {
    const wordCount = t.split(/\s+/).filter(Boolean).length;
    const hasSentenceWords = /\b(is|are|was|were|find|if|the|a|an|what|which|that|and|or|but|then|when|where|prove|show|given|equal|value|calculate|determine|number)\b/i.test(t);
    if (wordCount <= 15 && !hasSentenceWords) return `$${t}$`;
    t = t.replace(
      /((?:\\[a-zA-Z]+(?:{[^}]*})*|[a-zA-Z0-9])\s*(?:[+\-×÷/=<>^_]\s*(?:\\[a-zA-Z]+(?:{[^}]*})*|[a-zA-Z0-9{}\s]+))+)/g,
      (match) => match.trim().startsWith('$') ? match : `$${match.trim()}$`
    );
    return t;
  }
  return t;
}

// ─────────────────────────────────────────────────────────────────────────────
// MathRenderer — now accepts subjectId prop
// Only renders MathJax when subjectId === 3 (Mathematics)
// ─────────────────────────────────────────────────────────────────────────────
export default function MathRenderer({ text, subjectId, block = false, className = '' }) {
  const ref                   = useRef(null);
  const [visible, setVisible] = useState(false);

  const mathFound = hasMath(text, subjectId);   // ← passes subjectId into gate check
  const prepared  = mathFound ? prepareForMathJax(text) : null;

  useEffect(() => {
    if (!ref.current || !mathFound) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [mathFound]);

  useEffect(() => {
    if (!visible || !ref.current || !prepared) return;
    const doRender = () => {
      if (!window.MathJax?.typesetPromise) return false;
      ref.current.innerHTML = prepared;
      window.MathJax.typesetPromise([ref.current]).catch(() => {
        if (ref.current) {
          ref.current.innerHTML = prepared
            .replace(/\$\$?/g, '')
            .replace(/\\[a-zA-Z]+/g, ' ')
            .replace(/[{}^_]/g, '');
        }
      });
      return true;
    };
    if (!doRender()) {
      const timer = setInterval(() => { if (doRender()) clearInterval(timer); }, 100);
      return () => clearInterval(timer);
    }
  }, [visible, prepared]);

  if (!mathFound) return null;

  const Tag = block ? 'div' : 'span';
  return (
    <Tag
      ref={ref}
      className={`math-rendered ${className}`}
      style={{
        display  : block ? 'block' : 'inline',
        fontSize : '1em',
        lineHeight: 1.8,
        color    : 'inherit',
        overflowX: 'visible',
        maxWidth : '100%',
        wordBreak: 'break-word',
        minHeight: '1em',
      }}
    >
      {!visible && (
        <span style={{ opacity: 0.6, fontSize: '0.95em' }}>{text}</span>
      )}
    </Tag>
  );
}